import { useCallback } from 'react'

import { formatDate } from 'commons/utils/date'
import html2canvas from 'html2canvas'

/**
 * Custom hook that captures a screenshot of a specified DOM node and downloads it as an image.
 * @returns A function that can be called to capture and download the screenshot.
 * Captures and downloads a screenshot of the specified DOM node.
 * @param node - The DOM node to capture the screenshot of.
 * @example
 *   const screenshotComponentRef = useRef(null)
 *   const takeScreenshot = useTakeScreenshot()
 *
 *   const handleTakeScreenshot = useCallback(() => {
 *      takeScreenshot(screenshotComponentRef.current)
 *   }, [])
 */
export const useTakeScreenshot = () => {
  const captureAndDownload = useCallback(async (node: HTMLElement | null) => {
    if (!node) return

    try {
      const canvas = await html2canvas(node, {
        height: node.clientHeight,
        width: node.clientWidth,
      })
      const image = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream')
      const filename = `Screenshot ${formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')}.png`
      const link = document.createElement('a')

      link.download = filename
      link.href = image
      link.click()
    } catch (error) {
      console.error('Error capturing screenshot:', error)
    }
  }, [])

  return captureAndDownload
}
